import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  CalendarCheck, Contact, CornerDownLeft, FolderOpen, House, LineChart, Search, SlidersHorizontal, Table2, Upload, UserPlus, UsersRound, Workflow,
} from 'lucide-react';

import axiosInstance from 'src/common/axiosInstance';
import { AuthContext } from 'src/context/AuthContext';
import { Monogram } from 'src/stage/StageUi';
import { accessFor } from 'src/stage/crmNav';

// AUTO-CRM-32: ⌘K / Ctrl+K from anywhere. Find a lead by name, phone or
// company, jump to a page, or start a common task.
export const OPEN_PALETTE_EVENT = 'crm:open-palette';
export const QUICK_ADD_EVENT = 'crm:quick-add-lead';

export function openLead(navigate, lead) {
  const workspace = localStorage.getItem('autoappy_current_workspace_id') || '';
  const pipelineId = lead?.stage?.pipeline_id || lead?.pipeline_id;
  if (pipelineId) localStorage.setItem('selectedPipelineId', pipelineId);
  // The state switches an already-open lead list to the lead's pipeline.
  navigate(`/crm?lead=${lead.id}&workspace=${encodeURIComponent(workspace)}`, pipelineId ? { state: { pipelineId } } : undefined);
}

export default function CommandPalette({ onClose }) {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const access = accessFor(currentUser);
  const [query, setQuery] = useState('');
  const [leads, setLeads] = useState([]);
  const [searching, setSearching] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    const text = query.trim();
    if (text.length < 2) { setLeads([]); setSearching(false); return undefined; }
    setSearching(true);
    let active = true;
    const timer = setTimeout(() => {
      axiosInstance.get('/leads', { params: { search: text, per_page: 7 } })
        .then((response) => { if (active) setLeads(response.data?.data || []); })
        .catch(() => { if (active) setLeads([]); })
        .finally(() => { if (active) setSearching(false); });
    }, 200);
    return () => { active = false; clearTimeout(timer); };
  }, [query]);

  const commands = useMemo(() => {
    const go = (label, to, icon, hint) => ({ id: `go:${to}`, group: 'Go to', label, icon, hint, run: () => navigate(to) });
    const all = [
      { id: 'add-lead', group: 'Do', label: 'Add a lead', icon: UserPlus, hint: 'Name, phone and source', run: () => window.dispatchEvent(new Event(QUICK_ADD_EVENT)) },
      { id: 'import', group: 'Do', label: 'Import leads from a spreadsheet', icon: Upload, run: () => navigate('/crm', { state: { openImport: true } }) },
      { id: 'plan', group: 'Do', label: 'See today’s follow-ups', icon: CalendarCheck, run: () => navigate('/follow-ups') },
      access.canManagePipelines && { id: 'pipeline', group: 'Do', label: 'Create a pipeline from a template', icon: Workflow, run: () => navigate('/admin/pipelines?create=1') },
      go('Home', '/workspace', House),
      go('Leads', '/crm', Contact),
      go('Follow-ups', '/follow-ups', CalendarCheck),
      go('Insights', '/insights', LineChart),
      go('Documents', '/documents', FolderOpen),
      access.canViewTeams && go('Team', '/teams', UsersRound),
      access.canManagePipelines && go('Pipelines', '/admin/pipelines', Workflow),
      access.canManageLeadFields && go('Lead table', '/admin/lead-fields', Table2),
      go('Settings', '/settings', SlidersHorizontal),
    ].filter(Boolean);
    const text = query.trim().toLowerCase();
    return text ? all.filter((command) => command.label.toLowerCase().includes(text)) : all;
  }, [access.canManageLeadFields, access.canManagePipelines, access.canViewTeams, navigate, query]);

  const rows = useMemo(() => [
    ...leads.map((lead) => ({ id: `lead:${lead.id}`, group: 'Leads', lead, label: lead.name, run: () => openLead(navigate, lead) })),
    ...commands,
  ], [commands, leads, navigate]);

  useEffect(() => { setActive(0); }, [query, leads.length]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  const run = (row) => { onClose(); row.run(); };
  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') { event.preventDefault(); setActive((value) => Math.min(rows.length - 1, value + 1)); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setActive((value) => Math.max(0, value - 1)); }
    else if (event.key === 'Enter' && rows[active]) { event.preventDefault(); run(rows[active]); }
    else if (event.key === 'Escape') { event.preventDefault(); onClose(); }
  };

  let index = -1;
  const groups = ['Leads', 'Do', 'Go to'].map((group) => ({ group, items: rows.filter((row) => row.group === group) })).filter((group) => group.items.length);

  return createPortal(
    <div className="ws-dialog-backdrop ws-overlay crm-palette-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="crm-palette" role="dialog" aria-modal="true" aria-label="Search and commands" onKeyDown={onKeyDown}>
        <label className="crm-palette-search">
          <Search size={18} aria-hidden="true" />
          <input ref={inputRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search leads by name, phone or company, or type a command" aria-label="Search" role="combobox" aria-expanded="true" aria-controls="crm-palette-list" aria-activedescendant={rows[active] ? `crm-palette-${active}` : undefined} />
          <kbd>esc</kbd>
        </label>
        <div className="crm-palette-list" id="crm-palette-list" role="listbox" ref={listRef}>
          {query.trim().length >= 2 && !searching && !leads.length && <p className="crm-palette-empty">No leads match “{query.trim()}”.</p>}
          {searching && <p className="crm-palette-empty">Searching leads…</p>}
          {groups.map(({ group, items }) => (
            <div key={group} role="group" aria-label={group}>
              <div className="crm-palette-group">{group}</div>
              {items.map((row) => {
                index += 1;
                const current = index === active;
                const Icon = row.icon;
                return (
                  <div key={row.id} id={`crm-palette-${index}`} data-index={index} role="option" aria-selected={current} className={`crm-palette-row${current ? ' is-active' : ''}`}
                    onMouseEnter={((i) => () => setActive(i))(index)} onClick={() => run(row)}>
                    {row.lead ? <Monogram name={row.lead.name} size={28} /> : <span className="crm-palette-icon" aria-hidden="true"><Icon size={15} /></span>}
                    <span className="ws-grow">
                      <span className="crm-palette-label">{row.label}</span>
                      {row.lead && <span className="crm-palette-hint">{[row.lead.company, row.lead.phone, row.lead.stage?.name].filter(Boolean).join(' · ')}</span>}
                      {row.hint && <span className="crm-palette-hint">{row.hint}</span>}
                    </span>
                    {current && <CornerDownLeft size={14} className="crm-palette-enter" aria-hidden="true" />}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <footer className="crm-palette-foot"><span><kbd>↑</kbd><kbd>↓</kbd> to move</span><span><kbd>↵</kbd> to open</span><span><kbd>⌘</kbd><kbd>K</kbd> anytime</span></footer>
      </div>
    </div>,
    document.body,
  );
}
